import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';

import PageContainer from '../components/PageContainer';
import { PayrollRangePicker, PayrollRange, defaultPayrollRange } from '../components/payroll/PayrollRangePicker';
import { ChangeShiftTimes, PayrollShiftTable, PayrollTotalsGrid } from '../components/payroll/PayrollShiftTable';
import { RatesInRange } from '../components/payroll/RatesInRange';
import { Button, EmptyState, LoadingState, SectionPanel, useToast } from '../components/ui';
import { attendanceApi, payrollApi } from '../services/api';
import type { PersonPayroll } from '../types';
import { reportLink } from '../utils/reportLink';

// The signed-in person's own shifts and pay, read-only.
export default function MyPayroll() {
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [range, setRange] = useState<PayrollRange>(defaultPayrollRange);
    const [payroll, setPayroll] = useState<PersonPayroll | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const response = await payrollApi.getMine(range);
            setPayroll(response.data.data);
        } catch (error: any) {
            showToast(error?.message || 'Không tải được bảng công.', 'error');
        } finally {
            setLoading(false);
        }
    }, [range, showToast]);

    useEffect(() => {
        setLoading(true);
        load();
    }, [load]);

    // Same times as the attendance tab; managers get notified of the change there.
    const changeTimes: ChangeShiftTimes = async (shift, times) => {
        try {
            await attendanceApi.updateStaffTimes(shift.attendanceId, { slot: shift.slot === 'TEACHER' ? 'teacher' : 'assistant', ...times });
            showToast('Đã lưu giờ. Quản lý sẽ nhận thông báo về thay đổi này.', 'success');
            await load();
        } catch (error: any) {
            showToast(error?.message || 'Lưu giờ thất bại.', 'error');
            throw error;
        }
    };

    return (
        <PageContainer
            title="Bảng công của tôi"
            subtitle={(
                <>
                    Các ca bạn đã đăng ký dạy/trợ giảng và số tiền tương ứng.{' '}
                    <strong className="font-semibold text-danger-600">Liên hệ admin nếu có sai sót.</strong>
                </>
            )}
            actions={(
                <Button variant="secondary" icon={<Download className="h-4 w-4" />} onClick={() => navigate(reportLink('payroll-person', range))}>
                    Xuất Excel
                </Button>
            )}
        >
            <div className="flex flex-wrap items-center justify-between gap-4">
                <PayrollRangePicker value={range} onChange={setRange} />
                {payroll ? (
                    <div className="min-w-0 sm:min-w-[28rem]">
                        <RatesInRange rates={payroll.rates} from={payroll.from} />
                    </div>
                ) : null}
            </div>
            {loading && !payroll ? (
                <LoadingState title="Đang tải bảng công" />
            ) : !payroll ? (
                <EmptyState title="Không tải được bảng công" description="Thử tải lại trang." />
            ) : (
                <>
                    <PayrollTotalsGrid totals={payroll.totals} />
                    <SectionPanel className="overflow-hidden p-0 max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:shadow-none">
                        <PayrollShiftTable shifts={payroll.shifts} onChangeTimes={changeTimes} />
                    </SectionPanel>
                </>
            )}
        </PageContainer>
    );
}
